/* import __COLOCATED_TEMPLATE__ from './list.hbs'; */
/* RESPONSIBLE TEAM: team-messenger */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { type TaskGenerator } from 'ember-concurrency';
import { action } from '@ember/object';
import {
  matchBehaviors,
  objectTypes,
} from 'embercom/models/data/matching-system/matching-constants';
import { EVERYONE_PREDICATE } from 'embercom/lib/operator/custom-bots/constants';
import Ruleset from 'embercom/models/matching-system/ruleset';

interface Signature {
  Args: {};
  Blocks: {
    default: [];
  };
}

export default class MessengerNudgesList extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;

  @tracked nudgeRulesets: Array<any> = [];
  @tracked editingRuleset: any = null;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadNudges).perform();
  }

  get isLoading() {
    return taskFor(this.loadNudges).isRunning;
  }

  @dropTask
  *loadNudges(): TaskGenerator<void> {
    let response = yield get('/ember/messenger/nudges', {
      app_id: this.appService.app.id,
      admin_id: this.appService.app.currentAdmin.id,
    }) as Array<any>;

    this.nudgeRulesets = response.map((ruleset: any) => {
      return this.store.push(this.store.normalize('matching-system/ruleset', ruleset));
    });
  }

  @action async createNewNudge() {
    let ruleset = await Ruleset.createForType(this.store, {
      app_id: this.appService.app.id,
      object_type: objectTypes.nudge,
      match_behavior: matchBehaviors.transient,
      object_data: {},
      role_predicate_group: { predicates: [EVERYONE_PREDICATE] },
    });

    this.nudgeRulesets.pushObject(ruleset);

    this.editRuleset(ruleset);
  }

  @action editRuleset(ruleset: any) {
    this.editingRuleset = ruleset;

    this.nudgeRulesets = this.nudgeRulesets.filter(
      (ruleset) => !ruleset.isDestroyed && !ruleset.isDeleted,
    );
  }

  @action async onReorderNudges(reorderedRulesets: Array<any>, draggedRuleset: any) {
    // Update the local array to reflect the new order
    this.nudgeRulesets = reorderedRulesets;

    // Find the position of the dragged ruleset in the new order
    let draggedIndex = reorderedRulesets.indexOf(draggedRuleset);
    let previousRuleset = reorderedRulesets[draggedIndex - 1];
    let nextRuleset = reorderedRulesets[draggedIndex + 1];

    try {
      // Call the reorder method with the ruleset IDs
      await draggedRuleset.reorder(previousRuleset?.id, nextRuleset?.id);
    } catch (error) {
      // If reordering fails, reload the nudges to restore the correct order
      taskFor(this.loadNudges).perform();
      console.error('Failed to reorder nudges:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Messenger::Nudges::List': typeof MessengerNudgesList;
    'messenger/nudges/list': typeof MessengerNudgesList;
  }
}
